import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, ChevronRight, CheckCircle2, Shield, Package, Settings, Truck, Megaphone, Award, Target, BarChart3, RefreshCw } from 'lucide-react';

const ZomatoAccountManagement = () => {
  const services = [
    {
      title: 'Listing Optimization',
      items: ['Product title optimization', 'Description updates', 'Image management', 'Category mapping']
    },
    {
      title: 'Inventory Management',
      items: ['Stock monitoring', 'Replenishment planning', 'Out-of-stock prevention', 'Demand forecasting']
    },
    {
      title: 'Performance Tracking',
      items: ['Sales analytics', 'Conversion tracking', 'Customer insights', 'Growth reporting']
    }
  ];

  const whyChooseUs = [
    { icon: Award, title: 'Expert Management', description: 'Dedicated account managers' },
    { icon: BarChart3, title: 'Data-Driven', description: 'Analytics-based decisions' },
    { icon: RefreshCw, title: 'Continuous Optimization', description: 'Regular performance improvements' },
    { icon: Target, title: 'Growth Focused', description: 'Results-oriented strategies' }
  ];

  return (
    <div className="min-h-screen pt-32">
      <Helmet>
        <title>Zomato Marketplace Account Management | Obiquiste Solutions</title>
        <meta name="description" content="Expert Zomato marketplace account management: listing optimization, inventory management & performance tracking. Grow your Zomato business with us." />
        <link rel="canonical" href="https://www.obiquistesolutions.com/zomato-account-management" />
      </Helmet>
      {/* Breadcrumb */}
      <div className="bg-slate-50 py-4">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center text-sm text-slate-600">
            <Link to="/" className="hover:text-primary">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/services" className="hover:text-primary">Services</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-red-500 font-medium">Zomato Account Management</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-red-50 to-pink-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <BarChart3 className="w-4 h-4 mr-2" />
                Marketplace Management
              </div>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                Zomato <span className="text-red-500">Account Management</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed">
                Professional account management to grow your brand on Zomato marketplace. 
                Optimize listings, manage inventory, and drive sales growth.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact?audit=true">
                  <Button className="bg-red-500 text-white hover:bg-red-600 px-8 py-6 rounded-full text-base font-semibold">
                    Get Started
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-center mb-12">
            Our Services
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200"
              >
                <h3 className="font-heading text-xl font-bold mb-4 text-red-600">{service.title}</h3>
                <ul className="space-y-3">
                  {service.items.map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-center mb-12">
            Why Choose Us?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-md text-center"
              >
                <item.icon className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h3 className="font-heading font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-red-500 to-pink-500">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Grow on Zomato?
          </h2>
          <p className="text-red-100 text-lg mb-8 max-w-2xl mx-auto">
            Let our experts manage your Zomato account and drive business growth.
          </p>
          <Link to="/contact?audit=true">
            <Button className="bg-white text-red-600 hover:bg-slate-100 px-8 py-6 rounded-full text-base font-semibold">
              Get Free Consultation
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ZomatoAccountManagement;
